import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import moment from 'moment';
import { confirmAlert } from 'react-confirm-alert';

import { getApplyMode, setApplyMode, setApplyThreshold } from '../../api/url_data_agent_evaluations';
import ConfirmPrompt from '../General/ConfirmPrompt';

import './LiveMatchApplyModeControl.scss';

const REFRESH_MS = 30000;
const MODES = [
  { value: 'apply', label: 'Auto-match', summary: 'eligible matches above the minimum confidence are linked automatically and every other match goes straight to the catalog team' },
  { value: 'quarantine', label: 'Review first', summary: 'nothing is linked automatically: matches APM would link wait up to an hour for Auto-match, then go to the catalog team' },
  { value: 'paused', label: 'Send to team', summary: 'every suggested match goes straight to the catalog team' }
];
const THRESHOLDS = [
  {
    field: 'minConfidence',
    label: 'Minimum confidence',
    title: 'In Auto-match mode, eligible sampled matches at or above this confidence are applied automatically and the rest go to the catalog team. Empty sends them all to the catalog team.',
    emptyMeaning: 'send them all to the catalog team',
    clearedMessage: 'Sampled matches now all go to the catalog team.'
  },
  {
    field: 'minCreateConfidence',
    fallbackField: 'minConfidence',
    label: 'Create threshold',
    title: 'In Auto-match mode, eligible new products at or above this confidence are created automatically. Empty means they use the minimum confidence.',
    emptyMeaning: 'use the minimum confidence',
    clearedMessage: 'New products now use the minimum confidence.'
  }
];

// The auto-apply lane's switch. The workers read it every minute, so a flip takes effect within
// about 60 seconds without a deploy; the undo breaker flips it to Quarantine on its own.
const LiveMatchApplyModeControl = ({ onChange }) => {
  const [control, setControl] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [thresholdDrafts, setThresholdDrafts] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    onChange?.(control);
  }, [control, onChange]);

  const load = useCallback(async () => {
    try {
      const resp = await getApplyMode();
      setControl(resp.control || null);
      setLoadError(null);
    } catch (error) {
      setLoadError(error?.message || error || 'unknown error');
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, REFRESH_MS);
    return () => clearInterval(interval);
  }, [load]);

  const save = async (mode, reason) => {
    setIsSaving(true);
    try {
      const resp = await setApplyMode({ mode, reason });
      setControl(resp.control || null);
      window.ALERT.success(`Auto-apply set to ${mode}. Workers pick it up within a minute.`);
    } catch (error) {
      window.ALERT.error(`Failed to change the auto-apply mode: ${error?.message || error || 'unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  };

  const confirmMode = mode => {
    if (!control || isSaving || mode === control.mode) return;
    const target = MODES.find(option => option.value === mode);
    confirmAlert({
      customUI: ({ onClose }) => (
        <ConfirmPrompt
          header={`Set auto-apply to ${target.label}?`}
          subheader={`From the next worker tick, ${target.summary}.`}
          placeholder='Why? (saved on the switch)'
          rows={2}
          submitBtnDisplay={`Set to ${target.label}`}
          onCancel={onClose}
          onSubmit={reason => {
            onClose();
            save(mode, (reason || '').trim());
          }}
        />
      )
    });
  };

  const thresholdOptions = THRESHOLDS.map(option => {
    const stored = toPercent(control?.[option.field]);
    const draft = thresholdDrafts[option.field];
    const placeholder = (option.fallbackField && toPercent(control?.[option.fallbackField])) || 'off';
    return { ...option, placeholder, value: draft ?? stored, isDirty: draft != null && draft !== stored };
  });

  const setThresholdDraft = (field, value) => setThresholdDrafts(drafts => ({ ...drafts, [field]: value }));

  const saveThreshold = async ({ field, label, value, emptyMeaning, clearedMessage }) => {
    const confidence = value === '' ? null : Number(value) / 100;
    if (confidence !== null && !(confidence > 0 && confidence <= 1)) {
      window.ALERT.error(`Enter a confidence above 0% and at most 100%, or leave empty to ${emptyMeaning}.`);
      return;
    }
    setIsSaving(true);
    try {
      const resp = await setApplyThreshold({ [field]: confidence });
      setControl(resp.control || null);
      setThresholdDraft(field, null);
      window.ALERT.success(confidence === null ? clearedMessage : `${label} saved: ${value}%.`);
    } catch (error) {
      window.ALERT.error(`Failed to change the auto-apply threshold: ${error?.message || error || 'unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  };

  const status = control ? getStatusLine(control) : loadError ? `Couldn't load the switch: ${loadError}` : null;

  return (
    <div className={cn('lm-apply-mode', control?.mode)}>
      <span className='lm-apply-mode-label'>Matching mode</span>
      <div className='lm-apply-mode-segmented' role='group' aria-label='Auto-apply mode'>
        {MODES.map(option => (
          <button
            key={option.value}
            type='button'
            className={cn('lm-apply-mode-seg', { active: control?.mode === option.value })}
            disabled={!control || isSaving}
            title={option.summary}
            onClick={() => confirmMode(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
      {thresholdOptions.map(option => (
        <label key={option.field} className='lm-apply-mode-threshold' title={option.title}>
          {option.label}
          <input
            type='number'
            min='1'
            max='100'
            step='0.1'
            placeholder={option.placeholder}
            value={option.value}
            disabled={!control || isSaving}
            onChange={({ target }) => setThresholdDraft(option.field, target.value)}
            onKeyDown={event => event.key === 'Enter' && option.isDirty && saveThreshold(option)}
          />
          %
          {option.isDirty && (
            <button type='button' className='lm-apply-mode-threshold-save' disabled={isSaving} onClick={() => saveThreshold(option)}>
              Save
            </button>
          )}
        </label>
      ))}
      {control?.mode === 'apply' && control.minConfidence == null && control.minCreateConfidence == null && (
        <span className='lm-apply-mode-status'>Sampled matches go to the catalog team — no minimum confidence set.</span>
      )}
      {status && <span className={cn('lm-apply-mode-status', { breaker: Boolean(control?.breakerTrippedAt) })}>{status}</span>}
    </div>
  );
};

const toPercent = confidence => (confidence == null ? '' : String(Number((confidence * 100).toFixed(2))));

const getStatusLine = control => {
  const when = control.changedAt ? moment(control.changedAt).format('MMM D, HH:mm') : null;
  if (control.breakerTrippedAt) return `Breaker tripped${when ? ` ${when}` : ''}${control.reason ? ` · ${control.reason}` : ''}`;
  const who = control.changedBy?.name ? ` by ${control.changedBy.name}` : '';
  const reason = control.reason ? ` · ${control.reason}` : '';
  return when ? `Since ${when}${who}${reason}` : null;
};

LiveMatchApplyModeControl.propTypes = { onChange: PropTypes.func };

export default LiveMatchApplyModeControl;
